import React, { useEffect, useMemo, useState } from "react";
import { Box, Drawer, List, useTheme, useMediaQuery } from "@mui/material";
import styled from "@emotion/styled";
import { menus } from "../../data/menus";
import { filterMenusByPermissions, Menu } from "../../model/menu";
import { useAuth } from "../../../auth";
import { APPBAR_HEIGHT } from "../appbar";
import SidebarItem from "./sidebar-Item";
import SearchInput from "../../../core/components/input-fields/SearchInput";

export const drawerWidth = 250;

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const Sidebar = ({ mobileOpen, onMobileClose }: SidebarProps): JSX.Element => {
  const { session } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visibleMenu, setVisibleMenu] = useState<Menu[]>([]);

  const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    [`& .MuiDrawer-paper`]: {
      width: drawerWidth,
      boxSizing: "border-box",
      marginTop: isMobile ? 0 : APPBAR_HEIGHT + 12,
      borderTopRightRadius: isMobile ? 0 : 12,
      height: isMobile ? "100%" : "90%",
    },
  }));

  const handleItemClick = () => {
    if (isMobile) {
      onMobileClose();
    }
  };

  const drawer = (
    <Box sx={{ 
      overflow: "auto", 
      px: 2,
      pt: isMobile ? `${APPBAR_HEIGHT + 16}px` : 2
    }}>
      <SearchInput searchBy="name" data={menus} setDisplayedMenus={setVisibleMenu} />
      <List dense={true} sx={{ overflow: "auto" }}>
        {visibleMenu.map((menuItem, index) => (
          <SidebarItem 
            key={`${index}-${menuItem.name}`} 
            menu={menuItem} 
            mergedPath={""} 
            onItemClick={handleItemClick}
          />
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {/* Mobile drawer */}
      <StyledDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {drawer}
      </StyledDrawer>
      
      {/* Desktop drawer */}
      <StyledDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
        open
      >
        {drawer}
      </StyledDrawer>
    </Box>
  );
};

export default Sidebar;
